import { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { WHEN_LABELS } from '../api/v2ReportApi';
import type { V2AdvisorReport, V2ComingGroup } from '../api/v2AdvisorReportApi';
import { V2AdvisorNeedCard } from './V2AdvisorNeed';
import { PillarBar } from './V2ReportView';

const GREEN = '#026857';
const EYEBROW = 'text-[11px] font-semibold uppercase tracking-wider text-gray-500';

/** The consent date in UTC, as the PDF prints it: the viewer's zone could shift the day. */
const consentDate = (at: string) =>
  new Date(at).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

/** A "What's coming" timing group, folded behind its label, caption and count. */
function ComingGroup({ group }: { group: V2ComingGroup }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const n = group.needs.length;
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left hover:bg-[#fbf9f5]"
      >
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
        <span className="min-w-0 flex-1">
          <span className="font-serif text-[16px] font-semibold text-gray-900">
            {WHEN_LABELS[group.when]}
          </span>
          <span className="ml-2 text-sm text-gray-500">{group.guidance}</span>
        </span>
        <span className="shrink-0 rounded-full bg-[#f3efe6] px-2.5 py-1 text-xs font-medium text-gray-700">
          {n} {n === 1 ? 'need' : 'needs'}
        </span>
      </button>
      {open && (
        <ul id={panelId} className="border-t border-gray-100">
          {group.needs.map((need) => (
            <li
              key={need.needId}
              className="border-t border-gray-100 px-5 py-3.5 first:border-t-0 sm:pl-12"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-[15px] font-medium text-gray-900">{need.title}</span>
                <span className="text-xs text-gray-500">{need.pillarName}</span>
              </div>
              <p className="mt-1 text-[13px] leading-relaxed text-gray-600">
                <span className="font-semibold text-gray-700">Client will be asked to:</span>{' '}
                <span>{need.clientStep}</span>
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The advisor's report on an MVP 2 result, after Brooks's advisor report
 * designs: header with the household facts, pillar bars, the client's own top
 * three (the first open), then what is coming for the client by timing. Every
 * rule — which needs, in what order, which sections — is decided server-side,
 * so the screen and the PDF cannot disagree. No escalation block (removed in
 * review, Oct 20).
 */
export function V2AdvisorReportView({ report }: { report: V2AdvisorReport }) {
  const client = report.clientName;
  const topId = useId();
  const comingId = useId();
  return (
    <div className="mx-auto w-full max-w-4xl space-y-8">
      <section className="rounded-2xl border border-gray-200 bg-white p-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className={EYEBROW}>Advisor view · Readiness benchmark</div>
            <h1 className="mt-1 font-serif text-2xl text-gray-900">{client}</h1>
            {report.about && (
              <div className="mt-0.5 text-sm font-medium text-gray-800">About: {report.about}</div>
            )}
            <p className="mt-0.5 text-sm text-gray-500">
              {report.categoryLabel}
              {report.sharedAt && ` · Shared with consent ${consentDate(report.sharedAt)}`}
            </p>
          </div>
          <div className="text-right">
            <div className="font-serif text-4xl font-semibold" style={{ color: GREEN }}>
              {report.overallScore}
            </div>
            <div className="text-xs text-gray-500">out of 100</div>
          </div>
        </div>

        {report.household.length > 0 && (
          <dl className="mt-5 grid gap-x-8 gap-y-3 border-t border-gray-100 pt-5 sm:grid-cols-2">
            {report.household.map((fact) => (
              <div key={fact.label}>
                <dt className={EYEBROW}>{fact.label}</dt>
                <dd className="mt-0.5 text-[14px] text-gray-800">{fact.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {report.pillars.length > 0 && (
          <div className="mt-5 grid gap-4 border-t border-gray-100 pt-5 sm:grid-cols-5">
            {report.pillars.map((pillar) => (
              <PillarBar key={pillar.key} pillar={pillar} />
            ))}
          </div>
        )}
      </section>

      {report.topPriorities.length === 0 ? (
        <p className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
          No open priorities right now
        </p>
      ) : (
        <section aria-labelledby={topId} className="space-y-4">
          <div>
            <h2 id={topId} className="font-serif text-xl text-gray-900">
              {client}&rsquo;s top three needs
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              What bQuest asked {client} to do first, in the order they see it. Open each one for
              why it matters, what they were asked to do and a way to raise it.
            </p>
          </div>
          {report.topPriorities.map((need, i) => (
            <V2AdvisorNeedCard key={need.needId} need={need} defaultOpen={i === 0} />
          ))}
        </section>
      )}

      {report.comingGroups.length > 0 && (
        <section aria-labelledby={comingId} className="space-y-3">
          <div>
            <h2 id={comingId} className="font-serif text-xl text-gray-900">
              What&rsquo;s coming for {client}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Not yet on {client}&rsquo;s action list. Be ready to raise these as the first three
              settle.
            </p>
          </div>
          {report.comingGroups.map((group) => (
            <ComingGroup key={group.when} group={group} />
          ))}
        </section>
      )}

      <p className="text-center text-xs text-gray-600">
        Advisor use only, not for client distribution. {report.timingNote}
      </p>
    </div>
  );
}
